import {Component, computed, DestroyRef, effect, inject, OnDestroy, OnInit, Renderer2, signal, ViewChild} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { BreakpointObserver } from '@angular/cdk/layout';
import { MatExpansionModule } from '@angular/material/expansion';
import { ReactiveFormsModule } from '@angular/forms';
import { ScreenOrientationService, ToolbarEventsService } from 'src/app/core/services';
import { subscribeInComponent } from 'src/app/shared/angular-utils';
import { ColDef, FilterChangedEvent, FilterModel, GridOptions, GridState, ICellRendererParams, IRowNode, IServerSideDatasource, RowClickedEvent, RowGroupOpenedEvent, RowHeightParams } from 'ag-grid-community';
import { $state, restManager } from 'app/ajs-upgraded-providers';
import { from, map, tap } from 'rxjs';
import { BarcodeManagerService, EnvironmentInfoService, LoaderService, PaginatedResponse } from 'src/app/core';
import { BarcodeComponent, GridCellFormatterService, GridFixedButtons, GridServerSideComponent, headersTranslate } from 'src/app/shared/components';
import { ClickableActionStockMovements, GridClickableButtonStockMovementsComponent } from 'src/app/shared/components/grid/grid-clickable-button-stock-movements/grid-clickable-button-stock-movements.component';
import { QueryPagination, TableData } from 'src/app/shared/model/model';
import { SharedModule } from 'src/app/shared/shared.module';
import { StockMovementsFilterService } from './services/stock-movements-filter.service';
import { StockMovementsActionsService } from './services/stock-movements-actions.service';
import { StockMovementsStateService } from './services/stock-movements-state.service';
import { AddEditMovementDialogService } from 'src/app/dialogs/add-edit-movement-dialog/add-edit-movement-dialog.component';
import { StockMovementDetailFe, StockMovementSearchFe, StockMovementsFe, stockMovementsKeys } from './models/stock-movements.model';
import { GridCellValueGetterService } from 'src/app/shared/components/grid/utils/grid-cell-value-getter.service';
import { ConfigurationManagerService } from 'src/app/core/services/configuration-manager/configuration-manager.service';
import { ToolbarLayoutService } from 'src/app/shared/services/toolbar-layout.service';
import { GeneralInputButton } from '@tilby/tilby-ui-lib/models';
import { CommonModule } from '@angular/common';

// magazzino di vendita: la produzione manuale è possibile solo su questo
const SALES_LOCATION_ID = 1;

@Component({
	standalone: true,
  selector: 'app-stock-movements',
  templateUrl: './stock-movements.component.html',
  styleUrls: ['./stock-movements.component.scss'],
  imports: [ SharedModule, ReactiveFormsModule, MatExpansionModule, CommonModule, BarcodeComponent ],
  providers: [ StockMovementsFilterService, StockMovementsActionsService ]
})
export class StockMovementsComponent implements OnInit, OnDestroy {

  private readonly loaderService = inject(LoaderService);
  private readonly restManagerService = inject(restManager);
  private readonly state = inject($state);
  private readonly gridCellFormatterService = inject(GridCellFormatterService);
  private readonly gridValueGetterService = inject(GridCellValueGetterService);
  private readonly configurationManagerService = inject(ConfigurationManagerService);
  private readonly destroyRef = inject(DestroyRef);
  protected readonly filterService = inject(StockMovementsFilterService);
  private readonly actionsService = inject(StockMovementsActionsService);
  private readonly toolbarEventsService = inject(ToolbarEventsService);
  private readonly toolbarLayoutService = inject(ToolbarLayoutService);
  private readonly environmentInfoService = inject(EnvironmentInfoService);
  private readonly addEditMovementDialogService = inject(AddEditMovementDialogService);
  private readonly screenOrientationService = inject(ScreenOrientationService);
  private readonly stateService = inject(StockMovementsStateService);
  private readonly renderer = inject(Renderer2);
  private readonly barcodeManagerService = inject(BarcodeManagerService);
  private readonly breakpointObserver = inject(BreakpointObserver);

  protected readonly filterForm = this.filterService.createForm();

  protected readonly isSmallScreen = toSignal(
    this.breakpointObserver.observe('(max-width: 639px)').pipe(map(result => result.matches)),
    { initialValue: false }
  );
  protected isLandscape = computed(() => !!this.screenOrientationService.getOrientation().includes('landscape'));

  @ViewChild('stockMovemnetsGridComponent', {static: false}) gridRef!: GridServerSideComponent;
  @ViewChild('stockMovementsSearchGridComponent', {static: false}) searchGridRef?: GridServerSideComponent;

  readonly query: Readonly<QueryPagination> = {
    pagination: true,
    per_page: 30,
    page: 0,
  };

  protected noResults = false;
  protected batchesInitialState = this.buildInitialState(this.stateService.getState().batchesFilterModel);
  protected searchInitialState = this.buildInitialState(this.stateService.getState().searchFilterModel);

  private buildInitialState(filterModel?: FilterModel | null): GridState | undefined {
    return filterModel && Object.keys(filterModel).length ? { filter: { filterModel } } : undefined;
  }

  private isFirstLoad = true;
  
  datasource: IServerSideDatasource = {
    getRows: (params) => {
      if(this.isFirstLoad) {
        this.loaderService.show();
        this.isFirstLoad = false;
      }
      this.gridRef.getRowsForServersideDatasource(
        params,
        [{ ...this.query, ...this.filterService.buildQuery() } as QueryPagination],
        (a,b?)=> {
          return from(this.restManagerService.getList("stock_movements/batches", a) as Promise<PaginatedResponse<any>>).pipe(
            map((response: PaginatedResponse<any>) => {
              if (response?.results) {
                // un batch non ha un id: lo identificano i campi che lo raggruppano (stabile tra un caricamento e l'altro)
                response.results = response.results.map((item: any) => ({
                  ...item,
                  __batch_id: stockMovementsKeys.map(key => item[key] ?? '').join('|')
                }));
              }
              return response;
            }),
            tap({
              next: (response) => {
                if (!this.filterService.searchText()) {
                  this.noResults = !response?.results?.length && !response?.total;
                }
                this.loaderService.hide();
              },
              error: () => this.loaderService.hide()
            })
          );
        },
        {sort_type:"desc",sort_col:"date"}
      )
    }
  };

  searchDatasource: IServerSideDatasource = {
    getRows: (params) => {
      this.searchGridRef!.getRowsForServersideDatasource(
        params,
        [{ ...this.query, ...this.filterService.buildSearchQuery() } as QueryPagination],
        (a, b?) => {
          return from(this.restManagerService.getList("stock_movements", a) as Promise<PaginatedResponse<any>>).pipe(
            tap({
              next: (response) => {
                if (this.filterService.searchText()) {
                  this.noResults = !response?.results?.length && !response?.total;
                }
              }
            })
          );
        },
        {sort_type:"desc",sort_col:"date"}
      )
    }
  };

  searchTableData: TableData = {
    rowData$: null,
    dataType: new StockMovementSearchFe(),
    headersTranslate: headersTranslate.stock_movements_search,
    columnsFormatter: this.gridCellFormatterService.stock_movements_search,
    columnsValueGetter: this.gridValueGetterService.stock_movements_search,
    disabledInternalFilters: ['movement_type', 'row_amount'],
    sortingsMenuDisabled: new Map<string, boolean>([['movement_type', true], ['row_amount', true]]),
  };

  tableData: TableData[] = [
    {
      rowData$: null,
      dataType: new StockMovementsFe(),
      headersTranslate: headersTranslate.stock_movements_showcase,
      columnsFormatter: this.gridCellFormatterService.stock_movements,
      columnsValueGetter: this.gridValueGetterService.stock_movements,
      disabledInternalFilters: ['movement_type'],
      sortingsMenuDisabled: new Map<string, boolean>([['movement_type', true]]),
    }
  ];

  fixedButtons: GridFixedButtons = {
      checkbox: {visible: false, lockPosition: 'left'},
      customButton: {
        visible: true,
        cellRenderer: GridClickableButtonStockMovementsComponent,
        lockPosition: 'right'
      }
  };

  searchFixedButtons: GridFixedButtons = {
      checkbox: {visible: false, lockPosition: 'left'},
      customButton: {
        visible: false,
        cellRenderer: GridClickableButtonStockMovementsComponent,
        lockPosition: 'right'
      }
  };

  private readonly DETAIL_ROW_HEIGHT = 48;
  private readonly DETAIL_HEADER_HEIGHT = 56;
  private readonly DETAIL_VERTICAL_PADDING = 56;
  private readonly DETAIL_MAX_VISIBLE_ROWS = 5;

  // numero di righe caricate per ogni master (chiave: data del master), per ricalcolare l'altezza alla riapertura
  private readonly detailRowsCount = new WeakMap<object, number>();

  private readonly showPriceData = this.configurationManagerService.isModuleEnabled('items');

  protected detailTableData: TableData = {
    rowData$: null,
    dataType: new StockMovementDetailFe(),
    headersTranslate: headersTranslate.stock_movements_detail,
    columnsFormatter: this.gridCellFormatterService.stock_movements_detail,
    columnsValueGetter: this.gridValueGetterService.stock_movements_detail,
  };

  private detailGridOptions?: GridOptions;

  private getDetailHeight(rows: number): number {
    const visibleRows = Math.min(Math.max(rows, 1), this.DETAIL_MAX_VISIBLE_ROWS);
    return visibleRows * this.DETAIL_ROW_HEIGHT + this.DETAIL_HEADER_HEIGHT + this.DETAIL_VERTICAL_PADDING;;
  }

  // Master: altezza fissa per le righe di detail (con scroll interno oltre il massimo), default ag-grid per le altre
  protected getRowHeight = (params: RowHeightParams): number | undefined => {
    if (!params.node.detail) return undefined;

    return this.getDetailHeight(this.detailRowsCount.get(params.data) ?? 1);
  };

  private buildDetailGridOptions(): GridOptions {
    const columnDefs = this.gridRef.buildDetailColumnDefs(this.detailTableData).map((colDef): ColDef => {
      switch (colDef.field) {
        case 'name':
          return { ...colDef, cellRenderer: this.nameWithSkuRenderer, flex: 2 };
        case 'price':
        case 'perc_discount':
        case 'row_amount':
          return { ...colDef, hide: !this.showPriceData };
        default:
          return colDef;
      }
    });

    return {
      columnDefs,
      defaultColDef: {
        ...this.gridRef.detailDefaultColDef,
        menuTabs: ['filterMenuTab', 'columnsMenuTab'],
      },
      columnMenu: 'legacy',
      rowClass: 'ag-row-is-clickable',
      onRowClicked: (event) => this.openMovement(event.data, event.node),
      rowHeight: this.DETAIL_ROW_HEIGHT,
      headerHeight: this.DETAIL_HEADER_HEIGHT,
      localeText: this.gridRef.localText as any,
      suppressMovableColumns: true,
      suppressCellFocus: true
    };
  }

  // nome del prodotto con lo sku sulla seconda riga
  private nameWithSkuRenderer = (params: ICellRendererParams): HTMLElement => {
    const container = document.createElement('div');
    container.className = 'tw-flex tw-flex-col tw-justify-center tw-leading-tight tw-h-full';

    const name = document.createElement('span');
    name.textContent = params.data?.name ?? '';
    container.appendChild(name);

    if (params.data?.sku) {
      const sku = document.createElement('span');
      sku.className = 'tw-text-xs';
      sku.style.color = 'var(--sys-secondary-text)';
      sku.textContent = params.data.sku;
      container.appendChild(sku);
    }

    return container;
  };

  protected detailCellRendererParams = () => ({
    detailGridOptions: (this.detailGridOptions ??= this.buildDetailGridOptions()),
    getDetailRowData: (params: any) => {
      const queryObj: any = {
        pagination: false,
        orderby_desc: "date"
      };

      const batch = params.data;
      if (batch) {
        stockMovementsKeys.forEach(key => {
          if (batch[key] !== undefined) {
            queryObj[key] = batch[key];
          }
        });
      }

      this.restManagerService.getList('stock_movements', queryObj)
        .then((res: any) => {
          const rows = Array.isArray(res) ? res : (res?.results ?? []);
          this.setDetailHeight(params, rows.length);
          params.successCallback(rows);
        })
        .catch(() => {
          this.setDetailHeight(params, 0);
          params.successCallback([]);
        });
    }
  });

  private setDetailHeight(params: any, rows: number) {
    this.detailRowsCount.set(params.data, rows);

    params.node.setRowHeight(this.getDetailHeight(rows));
    this.gridRef.gridApi?.onRowHeightChanged();
  }

  private expandedMasterNode?: IRowNode;

  onRowGroupOpened(event: RowGroupOpenedEvent) {
    if (!event.expanded) {
      if (this.expandedMasterNode === event.node) {
        this.expandedMasterNode = undefined;
      }
      return;
    }

    const previous = this.expandedMasterNode;
    this.expandedMasterNode = event.node;

    if (previous && previous !== event.node && previous.expanded) {
      previous.setExpanded(false);
    }
  }

  exportFormatterOptions?: (...any: any[]) => any;
  
  protected gridPath = this.state.$current.name.split('.').pop() === 'movements';

  constructor() {
    subscribeInComponent(this.toolbarEventsService.events, (event) => {
      switch (event.type) {
        case 'toolbar.search_barcode':
          return this.applyBarcode(event.barcode);
        case 'toolbar.clear_search_bar':
          return this.clearBarcode();
        case 'toolbar.search':
          if (this.filterService.searchText() === event.search) return;

          const stayingInSearch = !!this.filterService.searchText() && !!event.search;
          this.filterService.searchText.set(event.search);
          this.stateService.updateState({ searchText: event.search });

          if (!stayingInSearch) {
            this.reloadInitialStates();
          }
          this.noResults = false;
          this.expandedMasterNode = undefined;
          return stayingInSearch ? this.refreshGrid() : undefined;
        case 'grid.open_tool_panel':
          return this.activeGrid?.openToolPanel(event.panel);
      }
    });

    effect(() => {
      [
        this.isLandscape(),
      ];

      setTimeout(() => {
        this.createToolbarButtons();
      }, 10)
    });
  }

  ngOnInit(): void {
    this.setModuleTitle();
    this.createToolbarButtons();

    this.toolbarEventsService.searchBarValue$.next(this.filterService.searchText());
    this.toolbarEventsService.searchBarcode$.next(false);
    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
    this.toolbarEventsService.searchBarAction$.next({ type: 'grid.open_tool_panel', panel: 'filters' });
    this.toolbarEventsService.searchBarActionFilled$.next({ type: 'grid.open_tool_panel', panel: 'filters' });

    this.filterService.loadStockLocations();
    this.filterService.filtersChanged$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshGrid());

  }

  setModuleTitle() {
    this.toolbarEventsService.moduleTitle.next("STOCK_MOVEMENTS");
  }

  createToolbarButtons() {
    const { allButtons, fixedButtons } = this.getToolbarConfig();
    this.toolbarLayoutService.updateToolbarLayout(allButtons, fixedButtons);
  }

  private getToolbarConfig() {
    const trueSignal = signal(true);
    
    const allButtons: GeneralInputButton[] = [
      {
        name: 'clear_filters',
        isIt: () => Object.keys(this.gridRef?.gridApi?.getFilterModel() || {}).length > 0,
        icon: signal('filter_alt_off'),
        label: signal('TOPBAR.ACTIONS.CLEAR_FILTERS'),
        click: () => this.clearGridFilters()
      },
      {
        name: 'clear_filters',
        isIt: () => Object.keys(this.searchGridRef?.gridApi?.getFilterModel() || {}).length > 0,
        icon: signal('filter_alt_off'),
        label: signal('TOPBAR.ACTIONS.CLEAR_FILTERS'),
        click: () => this.clearSearchGridFilters()
      },
      {
        name: 'search_barcode_apple',
        isIt: () => this.environmentInfoService.isAppleMobile(),
        iconType: 'symbols',
        icon: signal('barcode'),
        label: signal('TOPBAR.ACTIONS.BARCODE'),
        click: () => this.onBarcodeScannedAppleMobile()
      },
      {
        name: 'search_barcode_camera',
        isIt: () => this.environmentInfoService.isCameraBarcodeSupported(),
        icon: signal('photo_camera'),
        label: signal('TOPBAR.ACTIONS.PHOTO_CAMERA'),
        click: () => this.openBarcodeAndSearch()
      },
      {
        name: 'add_new_stock_movements',
        isIt: trueSignal,
        icon: signal('playlist_add'),
        label: signal('STOCK.NEW_MOVEMENT.NEW_MOVEMENT'),
        click: () => this.state.go('app.new.stock.new-movement', {
          stock_location_id: this.filterService.stock_location_id?.value
        })
      }
    ];

    const fixedButtons: GeneralInputButton[] = [
      {
        name: 'download_movements',
        isIt: () => this.environmentInfoService.canDownloadFiles(),
        icon: signal('file_download'),
        label: signal('STOCK.MOVEMENTS.OPTIONS.EXPORT'),
        click: () => this.actionsService.downloadMovements()
      },
      {
        name: 'share_movements',
        isIt: () => this.environmentInfoService.canShare(),
        icon: signal('share'),
        label: signal('GIFTCARD.SHOWCASE.SHARE'),
        click: () => this.actionsService.shareMovements()
      },
      {
        name: 'new_supplier_order',
        isIt: trueSignal,
        icon: signal('list_alt'),
        label: signal('STOCK.NEW_MOVEMENT.NEW_SUPPLIER_ORDER'),
        click: () => this.state.go('app.new.stock.new-movement', { mode: 'supplier_order' })
      },
      {
        name: 'item_production',
        isIt: () => this.filterService.stock_location_id?.value === SALES_LOCATION_ID,
        icon: signal('playlist_play'),
        label: signal('STOCK.CAUSES.PRODUCTION'),
        click: () => this.itemProduction()
      },
      {
        name: 'go_to_stock_management',
        isIt: trueSignal,
        icon: signal('content_paste'),
        label: signal('SIDENAV.MODULES.WAREHOUSE.STOCK'),
        click: () => this.goToStockManagement()
      },
      {
        name: 'import',
        isIt: trueSignal,
        icon: signal('upload'),
        label: signal('STOCK.TOPBAR_STOCK.IMPORT'),
        click: () => this.state.go('app.new.file_importer')
      }
    ];

    return { allButtons, fixedButtons };
  }

  async clearGridFilters() {
    const api = this.gridRef.gridApi;
    if (!api) return;
    api.setFilterModel(null);
  }

  async clearSearchGridFilters() {
    const api = this.searchGridRef?.gridApi;
    if (!api) return;
    api.setFilterModel(null);
  }

  private goToStockManagement() {
    this.state.go(this.configurationManagerService.isModuleAngular('stock.management')
      ? 'app.new.stock.management'
      : 'app.stock.management');
  }

  // griglia attualmente visibile: lista di ricerca se c'è testo, altrimenti master/detail
  private get activeGrid(): GridServerSideComponent | undefined {
    return this.filterService.searchText() ? this.searchGridRef : this.gridRef;
  }

  private refreshGrid() {
    const api = this.activeGrid?.gridApi;
    if (!api || api.isDestroyed()) {
      return;
    }
    this.expandedMasterNode = undefined;
    api.refreshServerSide({ purge: true });
  }

  onFilterChangedEmitter(event: FilterChangedEvent): void {
    if (!this.gridRef?.gridApi || this.gridRef.gridApi.isDestroyed()) {
      return;
    }

    this.stateService.updateState({ batchesFilterModel: this.gridRef.gridApi.getFilterModel() });
    this.createToolbarButtons();
  }

  onSearchFilterChanged(event: FilterChangedEvent): void {
    if (!this.searchGridRef?.gridApi || this.searchGridRef.gridApi.isDestroyed()) {
      return;
    }

    this.stateService.updateState({ searchFilterModel: this.searchGridRef.gridApi.getFilterModel() });
    this.createToolbarButtons();
  }

  rowClicked(params: RowClickedEvent) {
    if (this.state.router.globals.transition) {
      return; 
    }
    const isExpanded = params.node?.expanded;
    params.node?.setExpanded(!isExpanded);
  }

  searchRowClicked(params: RowClickedEvent) {
    this.openMovement(params.data, params.node);
  }

  // Dettaglio di un movimento (dalla tabella di dettaglio del batch o dalla lista di ricerca)
  private async openMovement(movement: any, node?: IRowNode) {
    if (!movement?.id || this.state.router.globals.transition) {
      return;
    }

    const result = await this.addEditMovementDialogService.openDialog({ data: { movement } });

    switch (result?.type) {
      case 'duplicate':
        return this.duplicateMovement(movement);
      case 'updated':
        // la nota è stata modificata: si aggiorna la riga con la risposta del server
        return node?.setData(result.movement);
    }
  }

  // un nuovo movimento parte da una copia di quello visualizzato, alla data corrente
  private duplicateMovement(movement: any) {
    const cloned = structuredClone(movement);
    cloned.date = new Date(new Date().setSeconds(0, 0)).toISOString();
    cloned.quantity = Math.abs(cloned.quantity);
    delete cloned.id;

    this.state.go('app.new.stock.new-movement', {
      movements: [cloned],
      stock_location_id: this.filterService.stock_location_id?.value
    });
  }

  // Produzione manuale da distinta base; a movimento creato si ricarica l'elenco
  private async itemProduction() {
    const result = await this.addEditMovementDialogService.openDialog({ data: { productionMode: true } });

    if (result?.type === 'created') {
      this.refreshGrid();
    }
  }

  actions({ action, data, node }: ClickableActionStockMovements) {
    if (!data) return;

    switch (action) {
      case 'details':
        return node.setExpanded(true);
      case 'export':
        return this.actionsService.exportBatch(data);
      case 'generate-labels':
        return this.actionsService.generateLabels(data);
      case 'rollback':
        return this.actionsService.rollbackBatch(data);
      case 'goods-delivery':
        return this.actionsService.goodsDelivery(data);
    }
  }

  // ------------------- barcode -------------------

  onBarcodeScanned(barcode: string) {
    if (document.querySelectorAll('.barcode-reader').length > 1) {
      return;
    }
    this.initBarcodeFilters(barcode);
  }

  private initBarcodeFilters(barcode: string) {
    this.toolbarEventsService.searchBarcode$.next(true);
    this.toolbarEventsService.searchBarValue$.next(barcode);
    this.toolbarEventsService.searchBarIcon$.next('');
    this.toolbarEventsService.searchBarIconFilled$.next('');
    this.toolbarEventsService.events.next({ type: 'toolbar.search_barcode', barcode });
  }

  private applyBarcode(barcode: string) {
    const wasSearching = !!this.filterService.searchText();

    this.filterService.barcode.set(barcode);
    this.filterService.searchText.set('');
    this.stateService.updateState({ searchText: '' });
    this.noResults = false;
    this.expandedMasterNode = undefined;

    if (wasSearching) {
      this.reloadInitialStates();
    } else {
      this.refreshGrid();
    }
  }

  // il barcode si toglie dalla "x" della barra
  private clearBarcode() {
    if (!this.filterService.barcode()) return;

    this.filterService.barcode.set('');
    this.toolbarEventsService.searchBarcode$.next(false);
    this.toolbarEventsService.searchBarIcon$.next('tune');
    this.toolbarEventsService.searchBarIconFilled$.next('tune');
    this.noResults = false;
    this.refreshGrid();
  }

  private async openBarcodeAndSearch() {
    try {
      const barcode = await this.barcodeManagerService.openCameraBarcode();

      if (barcode) {
        this.initBarcodeFilters(barcode);
      }
    } catch {
      // lettura annullata dall'utente
    }
  }

  onBarcodeScannedAppleMobile() {
    if (this.environmentInfoService.isAppleMobile()) {
      const barcodeInput = this.renderer.selectRootElement('#barcodeInput');
      barcodeInput.focus();
      barcodeInput.value = '';
    }
  }

  private reloadInitialStates() {
    this.batchesInitialState = this.buildInitialState(this.stateService.getState().batchesFilterModel);
    this.searchInitialState = this.buildInitialState(this.stateService.getState().searchFilterModel);
  }

  ngOnDestroy(): void {
    this.toolbarEventsService.searchBarValue$.next('');
    this.toolbarEventsService.searchBarcode$.next(false);
  }
}
